import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface Options {
    enabled?: boolean;
    header?: string | React.JSX.Element;
    message?: string | React.JSX.Element | ((...args: any[]) => string | React.JSX.Element);
    footer?: string | React.JSX.Element;
    destructive?: boolean;
}

export const useConfirmedCallback = (action: (...args: any[]) => any, options: Options = {}) => {
    const { t } = useTranslation('common');
    const { addModal, removeModal } = useModalManager();
    const { header, message, footer, enabled, destructive } = options;

    const confirmedAction = useCallback(
        (...args: any[]) => {
            if (enabled !== false) {
                // Resolved per call, not per render, so the dialog can name what THIS click affects.
                const resolvedMessage = typeof message === 'function' ? message(...args) : message;

                addModal({
                    title: header || t('confirmation.title'),
                    content: (id) => (
                        <>
                            {resolvedMessage ? (
                                typeof resolvedMessage === 'object' ? (
                                    resolvedMessage
                                ) : (
                                    <p className="tw-mb-4 tw-mt-2 tw-text-center">{resolvedMessage}</p>
                                )
                            ) : destructive ? (
                                <p className="tw-mb-4 tw-mt-2">
                                    {t('confirmation.destructiveMessage')}
                                </p>
                            ) : (
                                <p className="tw-mb-4 tw-mt-2">
                                    {t('confirmation.defaultMessage')}
                                </p>
                            )}
                            {footer || (
                                <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
                                    {!destructive ? (
                                        <>
                                            <Button
                                                flat
                                                size="sm"
                                                color="error"
                                                onPress={() => removeModal(id)}
                                                icon={<FontAwesomeIcon icon={faTimes} />}
                                            >
                                                {t('actions.cancel')}
                                            </Button>
                                            <Button
                                                flat
                                                size="sm"
                                                onPress={() => {
                                                    action(...args);
                                                    removeModal(id);
                                                }}
                                                icon={<FontAwesomeIcon icon={faCheck} />}
                                            >
                                                {t('actions.confirm')}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                flat
                                                size="sm"
                                                color="light"
                                                onPress={() => removeModal(id)}
                                                icon={<FontAwesomeIcon icon={faTimes} />}
                                            >
                                                {t('actions.cancel')}
                                            </Button>
                                            <Button
                                                flat
                                                size="sm"
                                                color="error"
                                                onPress={() => {
                                                    action(...args);
                                                    removeModal(id);
                                                }}
                                                icon={<FontAwesomeIcon icon={faCheck} />}
                                            >
                                                {t('actions.delete')}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    ),
                    show: true,
                    size: 30
                });
            } else {
                action(...args);
            }
        },
        [action, addModal, destructive, enabled, footer, header, message, removeModal, t]
    );

    return confirmedAction;
};
